(() => {
  if (customElements.get('product-card')) return;

  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
      this._syncVariantSelectFromActiveBtn =
        this._syncVariantSelectFromActiveBtn.bind(this);
    }

    connectedCallback() {
      this._cache();
      this._parseJSON();
      if (!this.data) return;

      const initialId = this._getActiveVariantId() || this.data.variants?.[0]?.id;
      if (initialId) {
        this._applyVariant(Number(initialId));
        this._syncVariantSelect(Number(initialId));
      }

      this.addEventListener('click', this._onClick);
      document.addEventListener('click', this._onDocClick);

      this.addEventListener('click', this._syncVariantSelectFromActiveBtn);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('click', this._syncVariantSelectFromActiveBtn);
      document.removeEventListener('click', this._onDocClick);
    }

    _cache() {
  this.$json = this.querySelector('script[data-product-card-json]');
  this.$links = this.querySelectorAll('a[data-role="link"]');
  this.$image = this.querySelector('[data-role="image"]');
  this.$price = this.querySelector('[data-role="price"]');
  this.$valuePrice = this.querySelector('[data-role="value-price"]');
  this.baseUrl = this.getAttribute('data-url-base') || this.getAttribute('data-url') || '';
  this.$variantImageWrap = this.querySelector('.absolute__variant--image');
  this.$variantImage = this.$variantImageWrap?.querySelector('img[data-role="image"]') || null;
  this.$priceLink =
    this.querySelector('[data-role="price-link"]') ||
    this.querySelector('.product-grid-item__price');

  this.$lastChance = this.querySelector('.last_chance');

  this.$variantSelect = this.querySelector('[data-role="variant-select"]');
  this.$selectTrigger = this.querySelector('[data-role="variant-select-trigger"]');
  this.$selectLabel = this.querySelector('[data-role="variant-select-label"]');
  this.$selectMenu = this.querySelector('[data-role="variant-select-menu"]');
}

    _parseJSON() {
      if (!this.$json) return;
      try {
        this.data = JSON.parse(this.$json.textContent.trim());
      } catch (e) {
        console.error('[product-card] invalid JSON', e);
        this.data = null;
      }
    }

    _getActiveVariantId() {
      const btn = this.querySelector('[data-role="variant-btn"].is-active');
      const id = btn?.getAttribute('data-variant-id');
      return id ? Number(id) : null;
    }

    _onClick(e) {
      const trigger = e.target.closest('[data-role="variant-select-trigger"]');
      if (trigger && this.contains(trigger)) {
        e.preventDefault();
        this._toggleSelect();
        return;
      }

      const option = e.target.closest('[data-role="variant-select-option"]');
      if (option && this.contains(option)) {
        e.preventDefault();

        if (
          option.classList.contains('is-disabled') ||
          option.getAttribute('aria-disabled') === 'true'
        ) return;

        const id = option.getAttribute('data-variant-id');
        if (!id) return;

        const btn = this.querySelector(
          `[data-role="variant-btn"][data-variant-id="${id}"]`
        );
        if (btn) btn.click();

        this._closeSelect();
        return;
      }

      const btn = e.target.closest('[data-role="variant-btn"]');
      if (!btn || !this.contains(btn)) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      const id = btn.getAttribute('data-variant-id');
      if (!id) return;

      this._setActive(btn);
      this._applyVariant(Number(id), { fromClick: true });
      this._syncVariantSelect(Number(id));
    }

    _onDocClick(e) {
      if (!this.$variantSelect) return;
      if (!this.contains(e.target)) {
        this._closeSelect();
      }
    }

    _toggleSelect() {
      if (!this.$selectTrigger || !this.$selectMenu) return;

      const isOpen = this.$selectTrigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) this._closeSelect();
      else this._openSelect();
    }

    _openSelect() {
      if (!this.$selectTrigger || !this.$selectMenu) return;
      this.$selectTrigger.setAttribute('aria-expanded', 'true');
      this.$selectMenu.hidden = false;
    }

    _closeSelect() {
      if (!this.$selectTrigger || !this.$selectMenu) return;
      this.$selectTrigger.setAttribute('aria-expanded', 'false');
      this.$selectMenu.hidden = true;
    }

    _setActive(activeBtn) {
      this.querySelectorAll('[data-role="variant-btn"]').forEach((b) => {
        const isActive = b === activeBtn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    _syncVariantSelectFromActiveBtn(e) {
      const btn = e.target.closest('[data-role="variant-btn"]');
      if (!btn || !this.contains(btn)) return;

      const id = btn.getAttribute('data-variant-id');
      if (!id) return;
      this._syncVariantSelect(Number(id));
    }

    _syncVariantSelect(variantId) {
      if (!this.$variantSelect) return;

      const optionEls = this.querySelectorAll('[data-role="variant-select-option"]');
      const activeOption = this.querySelector(
        `[data-role="variant-select-option"][data-variant-id="${variantId}"]`
      );

      if (this.$selectLabel) {
        const text =
          activeOption?.textContent?.trim() ||
          this.querySelector(`[data-role="variant-btn"][data-variant-id="${variantId}"]`)
            ?.textContent?.trim() ||
          '';
        if (text) this.$selectLabel.textContent = text;
      }

      optionEls.forEach((el) => {
        const isActive = el.getAttribute('data-variant-id') === String(variantId);
        el.classList.toggle('is-active', isActive);
        el.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    _applyVariant(variantId, { fromClick = false } = {}) {
      const variant = this.data?.variants?.find(v => Number(v.id) === Number(variantId));
      if (!variant) return;

      const secondaryBadges = this.querySelector('.product-badge--secondary');

      if (secondaryBadges) {
        secondaryBadges.hidden = variant.available;
        
        const customBadge = secondaryBadges?.querySelector('.product-badge__item--custom');

        if (fromClick && customBadge) customBadge.classList.add('hidden');
      };

      const baseUrl = this.baseUrl || '';

      if (baseUrl) {
        const base = new URL(baseUrl, window.location.origin);
        const links = this.querySelectorAll('a[href]');

        links.forEach((link) => {
          const href = link.getAttribute('href');
          if (!href) return;

          const u = new URL(href, window.location.origin);

          if (u.pathname === base.pathname) {
            u.searchParams.set('variant', variant.id);
            link.setAttribute('href', u.pathname + u.search);
            link.setAttribute('data-variant-id', String(variant.id));
          }
        });
      }

      if (this.$price) {
        let html = variant.price_html || '';
        if (variant.compare_at_price_html) {
          html += ` <s>${variant.compare_at_price_html}</s>`;
        }
        this.$price.innerHTML = html;
      }

      if (this.$valuePrice) {
        if (variant.value_price_html) {
      this.$valuePrice.innerHTML = variant.value_price_html;
      this.$valuePrice.style.display = '';
  } else {
    this.$valuePrice.style.display = 'none';
  }
}

      const $shortDesc = this.querySelector('[data-short-description]');
      if ($shortDesc) {
        const variantDesc = variant.short_description || '';
        const productDesc = $shortDesc.getAttribute('data-product-short-description') || '';
        const textToShow = variantDesc || productDesc;
        $shortDesc.textContent = textToShow;
        $shortDesc.style.display = textToShow ? '' : 'none';
      }

      if (this.$variantImageWrap && this.$variantImage) {
        const imgData = variant.image || null;

        if (imgData?.src) {
          this.$variantImage.setAttribute('src', imgData.src);

          if (imgData.srcset) this.$variantImage.setAttribute('srcset', imgData.srcset);
          else this.$variantImage.removeAttribute('srcset');

          this.$variantImageWrap.classList.add('active');
        } else {
          this.$variantImageWrap.classList.remove('active');
        }
      }

      const isOnSale =
        (typeof variant.compare_at_price === 'number' && Number(variant.compare_at_price) > Number(variant.price)) ||
        Boolean(variant.compare_at_price_html);
      const isLastChance = isOnSale && Boolean(this.data.is_last_chance);

      if (this.$priceLink) {
        if (!this.$lastChance) {
          this.$lastChance = document.createElement('span');
          this.$lastChance.className = 'last_chance';
          this.$lastChance.textContent = 'Last Chance';
          this.$priceLink.appendChild(this.$lastChance);
        }
        this.$lastChance.style.display = isLastChance ? '' : 'none';
      }
    }
  }

  customElements.define('product-card', ProductCard);
})();